import { useCallback, useContext, useEffect, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/react-hooks';

import { CartContext } from '../../../shared/contexts';
import { useCart, useModal, useMyStore } from '../hooks';

import {
  CREATE_DTCC_APPLICATION_URL,
  GET_DTCC_APPLICATION_STATUS
} from '../../graphql/dtcc-queries';

import { waitFor } from '../../utils/analytics/analytics';
import { loadScript, loadStyle } from '../../utils/page/page';

import { DTCC_APPLICATION_STATUS } from '../../constants/cart';

export default function useDTCCApplication() {
  const { myStore } = useMyStore();
  const { refreshCart } = useCart();
  const client = useApolloClient();
  const [isClientLoaded, setIsClientLoaded] = useState(false);
  const { dtccApplicationStatus, setDtccApplicationStatus } =
    useContext(CartContext);
  const { closeModal } = useModal();
  const [loadDtccApplicationData, { data, loading, error }] = useMutation(
    CREATE_DTCC_APPLICATION_URL
  );
  const storeCode = myStore?.code;

  const closureCallback = useCallback(() => {
    client
      .query({
        fetchPolicy: 'network-only',
        query: GET_DTCC_APPLICATION_STATUS,
        variables: {
          storeCode
        }
      })
      .then(response => {
        const status = response?.data?.synchrony?.getStatus?.status;
        setDtccApplicationStatus(status);
        if (status === DTCC_APPLICATION_STATUS.APPROVED) {
          refreshCart();
        } else if (status === DTCC_APPLICATION_STATUS.DECLINED) {
          loadDtccApplicationData({
            variables: {
              storeCode
            }
          });
        }
      });
    closeModal();
  }, [
    client,
    storeCode,
    closeModal,
    setDtccApplicationStatus,
    refreshCart,
    loadDtccApplicationData
  ]);

  const updateClient = useCallback(
    (applyNowButtonId, data) => {
      const applyNowData = data.synchrony?.createDTCCApplicationUrl;
      window.syfClient({
        closureCallback,
        dApplyOpener: applyNowButtonId,
        dApplyUrl: applyNowData.url
      });
    },
    [closureCallback]
  );

  function isReadyToSetClientLoaded() {
    return waitFor({
      condition: () => Boolean(window.syfClient),
      interval: 100,
      timeout: 10000,
      timeoutMessage: 'Timeout limit reached loading synchrony script'
    });
  }

  useEffect(() => {
    if (!window.syfClient && !window.syfLoading) {
      window.syfLoading = true;
      loadScript(
        {
          onload: () => {
            window.syfLoading = false;
            setIsClientLoaded(true);
          },
          src: process.env.__DTCC_INLINE_APPLY_SCRIPT_URL__
        },
        false
      );
      loadStyle(process.env.__DTCC_INLINE_APPLY_CSS_URL__);
    } else {
      (async () => {
        await isReadyToSetClientLoaded();
        setIsClientLoaded(true);
      })();
    }
  }, []);

  return {
    data,
    dtccApplicationStatus,
    error,
    isClientLoaded,
    loadDtccApplicationData,
    loading,
    setDtccApplicationStatus,
    updateClient
  };
}
